import { Button } from 'primereact/button'
import { Chip, type ChipProps } from 'primereact/chip'
import { Column } from 'primereact/column'
import { Dialog } from 'primereact/dialog'
import { Dropdown } from 'primereact/dropdown'
import { InputTextarea } from 'primereact/inputtextarea'
import { OverlayPanel } from 'primereact/overlaypanel'
import { SelectButton } from 'primereact/selectbutton'
import { TreeTable } from 'primereact/treetable'
import { type SyntheticEvent, useEffect, useId, useRef, useState } from 'react'
import Markdown from 'react-markdown'
import { useMutation, useQueryClient } from 'react-query'
import { useSearchParams } from 'react-router-dom'
import { CommentsDrawer } from '~/components/CommentsDrawer'
import ControlsDocumentDrawer, {
  type ControlStateType,
} from '~/components/ControlsPageDocumentsDrawer'
import { type Language, useLanguage } from '~/contexts/LanguageContext'
import { useControlList } from '~/hooks/controlsApi'
import { useModalControl } from '~/hooks/modalControl'
import useToast from '~/hooks/toast'
import { useUserStore } from '~/stores/userStore'
import { type ControlItem, ControlStatus } from '~/types/controls.types'
import { UserRole } from '~/types/user.types'
import httpClient from '~/utils/httpClient'

const titleRenderLang =
  (lang: Language) => (d: { data: ControlItem; children: { data: ControlItem }[] }) => {
    const guideModalCtl = useModalControl(`DescriptionModal:${d.data.title}`, null, false)

    const tooltipRef = useRef<OverlayPanel>(null)
    const rootProps = {
      onMouseEnter: (e: SyntheticEvent) => {
        tooltipRef.current?.show(e, e.currentTarget)
      },
      onMouseLeave: () => {
        tooltipRef.current?.hide()
      },
    }
    const titleText = lang === 'id' && d.data.titleId ? d.data.titleId : d.data.title
    const descriptionText =
      lang === 'id' && d.data.descriptionId ? d.data.descriptionId : d.data.description
    const tooltip = descriptionText && (
      <OverlayPanel
        ref={tooltipRef}
        pt={{ content: { className: 'p-2' }, root: { className: 'shadow-md bg-gray-200' } }}
      >
        <p className='max-w-80'>
          {lang === 'id' && d.data.descriptionId ? d.data.descriptionId : d.data.description}
        </p>
      </OverlayPanel>
    )
    const guideText =
      lang === 'id' && d.data.implementationGuideId
        ? d.data.implementationGuideId
        : d.data.implementationGuide
    const guideModal = guideText && (
      <Dialog
        visible={guideModalCtl.shown}
        onHide={guideModalCtl.hide}
        header={
          <div className='max-w-140'>
            <p>
              {d.data.code} - {titleText}
            </p>
            <p className='text-sm font-normal'>{descriptionText}</p>
          </div>
        }
      >
        <div className='max-w-160 markdownArea'>
          <Markdown>{guideText}</Markdown>
        </div>
      </Dialog>
    )
    return (
      <div>
        <span>
          {titleText}
          {descriptionText && <span className='pi pi-info-circle ml-1' {...rootProps} />}
          {guideText && (
            <Button
              icon='pi pi-book'
              rounded
              className='w-4 h-4 text-sm ml-1'
              link
              onClick={guideModalCtl.show}
            />
          )}
        </span>
        {tooltip}
        {guideModal}
      </div>
    )
  }

const getStatusChip = (status: ControlStatus, props?: ChipProps) => {
  const { className, ...restProps } = props ?? {}
  const allCn = `font-bold text-sm ${props?.onClick ? 'cursor-pointer' : ''} ${className}`
  switch (status) {
    case ControlStatus.Undecided:
      return <Chip className={`${allCn} bg-red-400`} label='Undecided' {...restProps} />
    case ControlStatus.NotApplicable:
      return <Chip className={`${allCn} bg-stone-300`} label='N/A' {...restProps} />
    case ControlStatus.DocumentListPending:
      return (
        <Chip className={`${allCn} bg-orange-400`} label='Pending Document List' {...restProps} />
      )
    case ControlStatus.DocumentsPending:
      return (
        <Chip className={`${allCn} bg-yellow-300`} label='Pending Document Draft' {...restProps} />
      )
    case ControlStatus.DocumentsReadyForAudit:
      return (
        <Chip className={`${allCn} bg-sky-400`} label='Document Waiting Audit' {...restProps} />
      )
    case ControlStatus.DocumentsPassedAudit:
      return (
        <Chip className={`${allCn} bg-green-400`} label='Document Passed Audit' {...restProps} />
      )
  }
}
const statusRenderLang =
  (lang: Language) =>
  (d: {
    data: ControlItem
    children: { data: ControlItem }[]
    setActiveDrawer: (c: ControlItem) => void
    openComments: (c: ControlItem) => void
  }) => {
    const userStore = useUserStore()
    const toast = useToast()
    const justificationInputId = useId()
    const [nextStatus, setNextStatus] = useState<ControlStatus>()
    const [justification, setJustification] = useState(d.data.justification ?? '')
    const titleText = lang === 'id' && d.data.titleId ? d.data.titleId : d.data.title
    const tooltipRef = useRef<OverlayPanel>(null)
    const tooltip = d.data.justification && (
      <OverlayPanel
        ref={tooltipRef}
        pt={{ content: { className: 'p-2' }, root: { className: 'shadow-md bg-gray-200' } }}
      >
        <p className='max-w-80'>{d.data.justification}</p>
      </OverlayPanel>
    )
    const statusModalCtl = useModalControl(`StatusModal:${d.data.title}`, null, false)
    const queryClient = useQueryClient()
    const submitMutation = useMutation({
      mutationFn: async ({
        id,
        status,
        justification,
      }: {
        id: number
        status: ControlStatus
        justification?: string
      }) => {
        await httpClient.patch(`/api/controls/item/${id}/status`, { status, justification })
        await queryClient.invalidateQueries(['controlState'])
      },
      onSuccess: () => {
        toast?.current?.show({ severity: 'success', summary: 'Successfully updated status' })
        statusModalCtl.hide()
      },
      onError: () => {
        toast?.current?.show({ severity: 'error', summary: 'Failed to update status' })
      },
    })
    const statusModal = (
      <Dialog
        visible={statusModalCtl.shown}
        onHide={statusModalCtl.hide}
        header={
          <div className='max-w-160 min-w-60'>
            <p>
              {d.data.code} - {titleText}
            </p>
            <p className='text-sm'>Change Status</p>
          </div>
        }
      >
        <p className='mb-4'>
          Current Status: <br />
          {getStatusChip(d.data.status)}
        </p>
        <p>Change Status To:</p>
        <Dropdown
          className='w-full mb-8'
          pt={{ item: { className: 'pl-2 py-1' }, input: { className: 'py-1' } }}
          options={[
            ControlStatus.NotApplicable,
            ControlStatus.DocumentListPending,
            ControlStatus.DocumentsPending,
            ControlStatus.DocumentsReadyForAudit,
            ControlStatus.DocumentsPassedAudit,
          ]}
          itemTemplate={getStatusChip}
          valueTemplate={(_, v) =>
            typeof v.value === 'number' ? getStatusChip(v.value) : 'Choose...'
          }
          onChange={(e) => setNextStatus(e.value)}
          value={nextStatus}
        />
        {nextStatus === ControlStatus.NotApplicable && (
          <div>
            <span className='p-float-label'>
              <InputTextarea
                className='w-full'
                id={justificationInputId}
                value={justification}
                onChange={(e) => setJustification(e.target.value)}
              />
              <label htmlFor={justificationInputId}>Justification</label>
            </span>
          </div>
        )}
        <div className='flex gap-2 justify-end'>
          <Button
            className='text-lg rounded-2xl py-2 px-4 font-bold'
            severity='secondary'
            onClick={statusModalCtl.hide}
          >
            Cancel
          </Button>
          <Button
            className='text-lg rounded-2xl py-2 px-4 font-bold'
            loading={submitMutation.isLoading}
            disabled={
              typeof nextStatus !== 'number' ||
              nextStatus === d.data.status ||
              (nextStatus === ControlStatus.NotApplicable &&
                justification.length < 20 &&
                justification === d.data.justification)
            }
            onClick={() => {
              submitMutation.mutate({ id: d.data.id, status: nextStatus ?? 0, justification })
            }}
          >
            Submit
          </Button>
        </div>
      </Dialog>
    )
    if (d.children.length) return <Chip className='bg-gray-100' label='(Group)' />
    const userIsAdmin = (userStore.user?.role ?? 0) === UserRole.Admin
    const documentsCount = (
      <Chip
        className={`bg-gray-100 ${userIsAdmin && 'cursor-pointer'}`}
        label={`${d.data.documents?.length ?? 0} Documents`}
        onClick={() => d.setActiveDrawer(d.data)}
      />
    )
    const allStatusChipProps = {
      onClick: userIsAdmin ? statusModalCtl.show : undefined,
    }
    const commentButton = (
      <Button
        icon='pi pi-comments'
        rounded
        text
        className='h-8 w-8'
        onClick={() => d.openComments(d.data)}
      />
    )
    switch (d.data.status) {
      case ControlStatus.NotApplicable:
        return (
          <div className='flex gap-1'>
            {statusModal}
            {tooltip}
            {getStatusChip(d.data.status, allStatusChipProps)}
            <Chip
              className='font-bold text-sm bg-stone-300'
              icon='pi pi-question-circle'
              label='Justification'
              onMouseEnter={(e) => {
                tooltipRef.current?.show(e, e.currentTarget)
              }}
              onMouseLeave={() => {
                tooltipRef.current?.hide()
              }}
            />
            {documentsCount}
            {commentButton}
          </div>
        )
      case ControlStatus.Undecided:
      case ControlStatus.DocumentListPending:
      case ControlStatus.DocumentsPending:
      case ControlStatus.DocumentsReadyForAudit:
      case ControlStatus.DocumentsPassedAudit:
        return (
          <div className='flex gap-1'>
            {statusModal}
            {tooltip}
            {getStatusChip(d.data.status, allStatusChipProps)}
            {documentsCount}
            {commentButton}
          </div>
        )
    }
    return d.data.status
  }

const ControlsPage = () => {
  const { lang, setLang } = useLanguage()
  const titleRender = titleRenderLang(lang)
  const statusRender = statusRenderLang(lang)
  const controlDefinitionsQ = useControlList()
  const [searchParams, setSearchParams] = useSearchParams()
  const [activeControlDocuments, setActiveControlDocuments] = useState<ControlStateType>(false)
  const [activeCommentTarget, setActiveCommentTarget] = useState<ControlItem | null>(null)

  const controlIdParam = searchParams.get('controlId')
  const documentIdParam = searchParams.get('documentId')
  const showCommentsParam = searchParams.get('showComments')

  useEffect(() => {
    if (controlIdParam && !documentIdParam) {
      const controlId = Number.parseInt(controlIdParam, 10)
      if (!Number.isNaN(controlId)) {
        const control = controlDefinitionsQ.arrayData?.find((c) => c.id === controlId)
        if (control) {
          if (showCommentsParam === 'true') {
            setActiveCommentTarget(control)
          } else {
            setActiveControlDocuments(control)
          }
        }
      }
    } else if (controlIdParam && documentIdParam) {
      const controlId = Number.parseInt(controlIdParam, 10)
      if (!Number.isNaN(controlId)) {
        const control = controlDefinitionsQ.arrayData?.find((c) => c.id === controlId)
        if (control) {
          setActiveControlDocuments(control)
        }
      }
    }
  }, [controlIdParam, documentIdParam, showCommentsParam, controlDefinitionsQ.arrayData])
  return (
    <div>
      <ControlsDocumentDrawer
        hide={() => {
          setActiveControlDocuments(false)
          setSearchParams((prev) => {
            const next = new URLSearchParams(prev)
            next.delete('controlId')
            next.delete('documentId')
            next.delete('showComments')
            return next
          })
        }}
        control={activeControlDocuments}
      />
      <CommentsDrawer
        visible={!!activeCommentTarget}
        onHide={() => {
          setActiveCommentTarget(null)
          setSearchParams((prev) => {
            const next = new URLSearchParams(prev)
            next.delete('controlId')
            next.delete('showComments')
            return next
          })
        }}
        control={activeCommentTarget || undefined}
      />
      <div className='flex w-full justify-between mb-4 items-baseline'>
        <h1 className='text-3xl'>Controls List</h1>
        <div className='flex gap-1'>
          <Button icon='pi pi-refresh' link onClick={controlDefinitionsQ.fullRefetch} />
          <SelectButton
            options={[
              { label: 'En', value: 'en' },
              { label: 'Id', value: 'id' },
            ]}
            value={lang}
            onChange={(e) => e.value && setLang(e.value)}
          />
        </div>
      </div>
      <TreeTable key={lang} value={controlDefinitionsQ.treeData ?? []}>
        <Column header='Code' key='code' field='code' expander />
        <Column header='Title' key='title' field='title' body={titleRender} />
        <Column
          header='Status'
          key='status'
          field='status'
          body={(d) =>
            statusRender({
              ...d,
              setActiveDrawer: setActiveControlDocuments,
              openComments: setActiveCommentTarget,
            })
          }
        />
      </TreeTable>
    </div>
  )
}

export default ControlsPage
